/**
 * diffRegions.js — Client-side pixel-diff for revision comparison.
 *
 * Visual field coordination, NOT document analysis software.
 *
 * Approach (tile-based, deliberately coarse so it FEELS like the eye-help it
 * actually is — not a "machine-detected objects" panel):
 *   1. Decode both rasters at a downscaled working size (~640px wide) to keep
 *      the entire diff under ~50ms on a tablet.
 *   2. Subdivide the working canvas into TILE × TILE blocks (default 12 px).
 *   3. For each tile, compute the mean luma delta between the two images
 *      AND the directional sign (is the new image darker or lighter at this
 *      tile's pixels? — drawings are black ink on white, so "darker than
 *      before" = something added, "lighter than before" = something removed).
 *   4. Apply a sensitivity threshold to flag tiles as added / removed.
 *   5. Union-find merge adjacent tiles of the same type into rectangles.
 *   6. Drop rectangles smaller than ~3 tiles² (noise).
 *   7. Return list of { type: "added"|"removed", nx, ny, nw, nh } in
 *      sheet-normalized 0..1 space — independent of viewport, so SVG can
 *      render them at any zoom level.
 *
 * The output is intentionally a small list (typically 5-30 rects) — the
 * overlay should HELP the eye, not paint the whole sheet.
 */

// 1024px working width — wide enough that thin (≈1pt) revision-cloud and
// callout strokes on a real heavy-civil 2400px raster downscale to ≥1px wide
// in the working buffer. 640px lost them entirely. Cost vs the old 640px is
// ~2.5× pixels, still well under the ~80ms total budget on a tablet.
const WORK_W = 1024;
const TILE = 10;

/**
 * Compute diff regions between two image URLs.
 *
 * @param {string} url1     - previous-revision raster URL
 * @param {string} url2     - current-revision raster URL
 * @param {object} opts     - { sensitivity:0..1, signal?:AbortSignal }
 * @returns {Promise<{regions:Array, dims:{w,h}}>}
 */
export async function computeDiffRegions(url1, url2, { sensitivity = 0.5, signal } = {}) {
  if (!url1 || !url2) return { regions: [], dims: { w: 0, h: 0 } };

  const [im1, im2] = await Promise.all([loadImage(url1), loadImage(url2)]);
  if (signal?.aborted) throw new DOMException("aborted", "AbortError");

  // Pick aspect from the first image; scale both into the same working size.
  const aspect = im1.naturalHeight / im1.naturalWidth;
  const w = WORK_W;
  const h = Math.max(64, Math.round(WORK_W * aspect));

  const a = canvasFrom(im1, w, h);
  const b = canvasFrom(im2, w, h);
  const dataA = a.ctx.getImageData(0, 0, w, h).data;
  const dataB = b.ctx.getImageData(0, 0, w, h).data;

  // sensitivity (0..1) → luma threshold. Floor lowered from 8→3 so real
  // operational annotation strokes (rev clouds, callout leaders) actually
  // surface on real plans — they were dropping below the floor entirely on
  // pure black-ink-on-white drawings.
  const threshold = 3 + (1 - sensitivity) * 28;

  const tilesX = Math.ceil(w / TILE);
  const tilesY = Math.ceil(h / TILE);
  // type: 0=unchanged, 1=added, 2=removed
  const grid = new Uint8Array(tilesX * tilesY);

  // Pass 1 — per-tile mean delta + direction.
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      const x0 = tx * TILE;
      const y0 = ty * TILE;
      const x1 = Math.min(w, x0 + TILE);
      const y1 = Math.min(h, y0 + TILE);
      let sumDelta = 0;
      let sumSigned = 0;
      let n = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const i = (y * w + x) * 4;
          const lA = 0.299 * dataA[i] + 0.587 * dataA[i + 1] + 0.114 * dataA[i + 2];
          const lB = 0.299 * dataB[i] + 0.587 * dataB[i + 1] + 0.114 * dataB[i + 2];
          const d = lB - lA;
          sumDelta += Math.abs(d);
          sumSigned += d;
          n++;
        }
      }
      const meanAbs = sumDelta / n;
      const meanSigned = sumSigned / n;
      if (meanAbs >= threshold) {
        // Classify directionally:
        //   - Strongly darker  → 1 = ADDED ink   (red cloud)
        //   - Strongly lighter → 2 = REMOVED ink (blue cloud)
        //   - Mixed (both directions inside the tile, mean signed near zero
        //     but mean abs high) → 3 = MODIFIED (yellow hatch). This is what
        //     a curb-line shift or a re-routed pipe looks like — ink moved
        //     within the same area, neither purely added nor purely
        //     removed.
        const directional = Math.abs(meanSigned) / Math.max(1, meanAbs);
        if (directional < 0.45) grid[ty * tilesX + tx] = 3;
        else grid[ty * tilesX + tx] = meanSigned < 0 ? 1 : 2;
      }
    }
  }

  // Pass 2 — union-find merge adjacent same-type tiles into bounding boxes.
  const visited = new Uint8Array(tilesX * tilesY);
  const regions = [];
  const stack = [];
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      const idx0 = ty * tilesX + tx;
      const type = grid[idx0];
      if (!type || visited[idx0]) continue;
      // BFS flood-fill same-type tiles.
      stack.length = 0;
      stack.push(idx0);
      let minX = tx, maxX = tx, minY = ty, maxY = ty, count = 0;
      while (stack.length) {
        const idx = stack.pop();
        if (visited[idx]) continue;
        visited[idx] = 1;
        const cx = idx % tilesX;
        const cy = (idx - cx) / tilesX;
        if (grid[idx] !== type) continue;
        if (cx < minX) minX = cx;
        if (cx > maxX) maxX = cx;
        if (cy < minY) minY = cy;
        if (cy > maxY) maxY = cy;
        count++;
        if (cx > 0)             stack.push(idx - 1);
        if (cx < tilesX - 1)    stack.push(idx + 1);
        if (cy > 0)             stack.push(idx - tilesX);
        if (cy < tilesY - 1)    stack.push(idx + tilesX);
      }
      // Filter tiny noise — require at least ~4 tile-cells AND a min
      // bounding-box edge of 2 tiles. This keeps rev-cloud-sized changes
      // visible (the cloud cross-section samples ~5-8 tiles) while still
      // dropping single-tile JPEG compression noise.
      if (count < 4) continue;
      if ((maxX - minX) < 1 && (maxY - minY) < 1) continue;
      const pxX0 = (minX * TILE);
      const pxY0 = (minY * TILE);
      const pxX1 = Math.min(w, (maxX + 1) * TILE);
      const pxY1 = Math.min(h, (maxY + 1) * TILE);
      regions.push({
        type: type === 1 ? "added" : type === 2 ? "removed" : "modified",
        nx: pxX0 / w,
        ny: pxY0 / h,
        nw: (pxX1 - pxX0) / w,
        nh: (pxY1 - pxY0) / h,
      });
    }
  }

  // Cap at 60 rects total — anything past that is JPEG compression noise.
  regions.sort((r1, r2) => (r2.nw * r2.nh) - (r1.nw * r1.nh));
  return { regions: regions.slice(0, 60), dims: { w, h } };
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function canvasFrom(img, w, h) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  return { ctx };
}
